export const CONTACT_CHANNELS: ContactChannel[] = [
  'email',
  'sms',
  'voice',
  'pager',
];

export const CONTACT_CHANNEL_LABELS: Record<ContactChannel, string> = {
  email: 'Email',
  sms: 'Text message',
  voice: 'Phone call',
  pager: 'Pager',
};

/**
 * Channels that may not be used without the recipient's consent, so the editor
 * shows an opt-in control for them and only for them.
 */
export const CONSENT_CHANNELS: ContactChannel[] = ['sms', 'voice'];

export function requiresConsent(channel: ContactChannel): boolean {
  return CONSENT_CHANNELS.includes(channel);
}

/** Mirrors the server's rule, so the UI can say why a method is unreachable. */
export function isSendable(method: ContactMethod): boolean {
  if (method.opted_out) return false;
  return requiresConsent(method.channel) ? method.opted_in : true;
}

export function methodsForChannel(
  methods: ContactMethod[] | undefined,
  channel: ContactChannel
): ContactMethod[] {
  return (methods ?? [])
    .filter((method) => method.channel === channel)
    .sort((a, b) => a.position - b.position);
}

/** The address a single-destination send would use, or undefined. */
export function primaryValue(
  methods: ContactMethod[] | undefined,
  channel: ContactChannel
): string | undefined {
  return methodsForChannel(methods, channel).filter(isSendable)[0]?.value;
}

/**
 * Every value on a channel, joined for display in a table cell. A user can hold
 * more than one number now, and showing only the first would hide the rest.
 */
export function describeChannel(
  methods: ContactMethod[] | undefined,
  channel: ContactChannel
): string {
  return methodsForChannel(methods, channel)
    .map((method) => method.value)
    .join(', ');
}

/**
 * Phone numbers a legacy notification compose screen can dial, in send order
 * and across both phone channels, de-duplicated because a mobile is stored once
 * per channel.
 */
export function phoneNumbersForUser(
  methods: ContactMethod[] | undefined
): string[] {
  const numbers = [
    ...methodsForChannel(methods, 'sms'),
    ...methodsForChannel(methods, 'voice'),
  ]
    .filter(isSendable)
    .map((method) => method.value);

  return [...new Set(numbers)];
}
